/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import Select, { StylesConfig } from 'react-select';
import { Button, Form, FormGroup, FormText, Input, Label } from 'reactstrap';
import variables from '../../../css/variables.module.scss';
import { getCurrencyOptions } from '../../../utils/units';
import { useLoadCommonDataQuery } from '@api/common';
import { getOnboardingCountries } from '@utils/onboarding';
import { homepageViews } from '@routes/home/flavours/config';
import { BasicAlert } from '@g17eco/molecules/alert';
import { Option, SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { useSearchParams } from 'react-router-dom-v5-compat';
import { CompanyInfoFormProps, useGetCompanyInfo } from '../hooks/useGetCompanyInfo';

export const getStyles = (): StylesConfig<any> => {
  return {
    placeholder: (provided) => ({
      ...provided,
      color: variables.TextPlaceholder,
    }),
  };
};

type WrongEnvironmentAlertProps = {
  selectedCountry: Option<string>;
};
const WrongEnvironmentAlert = ({
  selectedCountry,
  formData,
}: Pick<CompanyInfoFormProps, 'formData'> & WrongEnvironmentAlertProps) => {
  const { origin: currentOrigin } = window.location;
  const { url: suggestionUrl, title: suggestionTitle } =
    homepageViews.find((view) => view.locationMatch?.country?.includes(selectedCountry.value)) ?? {};
  const { url: currentUrl, title: currentTitle } = homepageViews.find((view) => view.url === currentOrigin) ?? {};

  const [visible, setVisible] = useState(currentUrl !== suggestionUrl);
  const [_searchParams, setSearchParams] = useSearchParams();

  if (!visible || !suggestionUrl || !currentUrl) {
    return null;
  }

  const handleSwitch = () => {
    const { referenceCompany, referralCode, initiativeCountryCode } = formData;
    setSearchParams({ referenceCompany, referralCode, initiativeCountryCode, switch: 'true' });

    const { pathname, search } = window.location;
    window.location.href = `${suggestionUrl}${pathname}${search}`;
  };

  return (
    <BasicAlert type='warning'>
      <p className='text-ThemeWarningExtradark'>
        We noticed you’ve selected in {selectedCountry.label}. Would you prefer to use our {suggestionTitle} platform?
        (recommended)
      </p>
      <div className='d-flex justify-content-between gap-3'>
        <Button className='flex-grow-1' size='sm' color='warning' outline onClick={() => setVisible(false)}>
          Stay on {currentTitle}
        </Button>
        <Button className='flex-grow-1' size='sm' color='warning' onClick={handleSwitch}>
          Switch to {suggestionTitle}
        </Button>
      </div>
    </BasicAlert>
  );
};

const CountrySelect = ({
  updateForm,
  formData,
  isRedirected,
  showLabel = true,
}: Pick<CompanyInfoFormProps, 'formData' | 'updateForm'> & {
  isRedirected: boolean;
  showLabel?: boolean;
}) => {
  const { data: commonData } = useLoadCommonDataQuery();
  const countries = getOnboardingCountries(commonData?.countries);
  const selectedCountry = countries.find((c) => c.value === formData.initiativeCountryCode);

  const currentPlatform = isRedirected
    ? homepageViews.find((view) => view.url === window.location.origin)?.title
    : undefined;

  return (
    <div className='col'>
      <FormGroup>
        {showLabel ? <Label for='initiativeCountryCode'>Country</Label> : null}
        <SelectFactory
          selectType={SelectTypes.SingleSelect}
          placeholder={'Select country from dropdown'}
          options={countries}
          value={selectedCountry}
          onChange={(option) => {
            updateForm('initiativeCountryCode', option?.value ?? '');
          }}
        />
      </FormGroup>

      {selectedCountry ? (
        <WrongEnvironmentAlert key={selectedCountry.value} selectedCountry={selectedCountry} formData={formData} />
      ) : null}
      {isRedirected ? (
        <BasicAlert type='success'>You have been successfully switched to the {currentPlatform} platform.</BasicAlert>
      ) : null}
    </div>
  );
};

const CompanyName = ({ updateForm, formData }: Pick<CompanyInfoFormProps, 'formData' | 'updateForm'>) => {
  return (
    <div className='row mb-3'>
      <div className='col'>
        <FormGroup>
          <Label for='initiativeName'>What is your company or organisation name?</Label>
          <Input
            id='initiativeName'
            name='initiativeName'
            type='text'
            placeholder='e.g. Alphabet Inc.'
            onChange={(e) => updateForm('initiativeName', e.target.value)}
            value={formData.initiativeName}
            required
          />
        </FormGroup>
      </div>
    </div>
  );
};

const CompanyAddress = ({ updateForm, formData }: Pick<CompanyInfoFormProps, 'formData' | 'updateForm'>) => {
  const addressData = formData.address;
  const handleAddressInput = (name: string, value: any) => {
    updateForm('address', {
      ...formData['address'],
      [name]: value,
    });
  };

  return (
    <>
      <div className='row mt-3'>
        <div className='col'>
          <FormGroup className='mb-1'>
            <Label for='line1'>What is your HQ address?</Label>
            <Input
              id='line1'
              name='line1'
              type='text'
              placeholder='First line of address'
              onChange={(e) => handleAddressInput('line1', e.target.value)}
              value={addressData?.line1 ?? ''}
              required
            />
          </FormGroup>
        </div>
      </div>
      <div className='row'>
        <div className='col'>
          <FormGroup className='mb-1'>
            <Input
              id='line2'
              name='line2'
              type='text'
              placeholder='Second line of address'
              onChange={(e) => handleAddressInput('line2', e.target.value)}
              value={addressData?.line2 ?? ''}
              required
            />
          </FormGroup>
        </div>
      </div>
      <div className='row'>
        <div className='col'>
          <FormGroup className='mb-1'>
            <Input
              id='city'
              name='city'
              type='text'
              placeholder='Name of town or city'
              onChange={(e) => handleAddressInput('city', e.target.value)}
              value={addressData?.city ?? ''}
              required
            />
          </FormGroup>
        </div>
      </div>
      <div className='row'>
        <div className='col'>
          <FormGroup>
            <Input
              id='postcode'
              name='postcode'
              type='text'
              placeholder='Postal code / Zip code etc'
              onChange={(e) => handleAddressInput('postcode', e.target.value)}
              value={addressData?.postcode ?? ''}
              required
            />
          </FormGroup>
        </div>
      </div>
    </>
  );
};

const CompanyCurrency = ({ updateForm, formData }: Pick<CompanyInfoFormProps, 'formData' | 'updateForm'>) => {
  const currencyOptions = getCurrencyOptions();

  return (
    <div className='row mb-3'>
      <div className='col'>
        <FormGroup>
          <Label for='currency'>Reporting currency</Label>
          <Select
            placeholder={'e.g. US Dollars'}
            options={currencyOptions}
            styles={getStyles()}
            value={currencyOptions.find((c) => c.value === formData.unitConfig?.currency) ?? null}
            onChange={(option) => updateForm('unitConfig', { ...formData.unitConfig, currency: option?.value ?? '' })}
          />
        </FormGroup>
      </div>
    </div>
  );
};

const CompanyRegistrationNumber = ({ updateForm, formData }: Pick<CompanyInfoFormProps, 'formData' | 'updateForm'>) => {
  return (
    <div className='row mb-3'>
      <div className='col'>
        <FormGroup>
          <Label for='registrationNumber'>Company registration number</Label>
          <Input
            placeholder='Enter unique company number if known'
            id='registrationNumber'
            name='registrationNumber'
            type='text'
            onChange={(e) => updateForm('registrationNumber', e.target.value)}
            value={formData.registrationNumber}
          />
          <FormText>optional</FormText>
        </FormGroup>
      </div>
    </div>
  );
};

const FormFooter = ({
  handlePrevious,
  handleCreateCompany,
  isFormValid,
}: Pick<CompanyInfoFormProps, 'handlePrevious' | 'handleCreateCompany'> & {
  isFormValid: boolean;
}) => {
  const handleNext = (e: React.ChangeEvent<any>) => {
    e.preventDefault();
    if (isFormValid) {
      handleCreateCompany();
    }
  };

  return (
    <div className='mt-5 text-right d-flex'>
      {handlePrevious ? (
        <Button color='link-secondary' onClick={handlePrevious}>
          <i className='far fa-angle-left mr-2' />
          Back
        </Button>
      ) : null}
      <Button color='primary' disabled={!isFormValid} className='ml-auto' onClick={handleNext}>
        Next
        <i className='far fa-angle-right ml-2' />
      </Button>
    </div>
  );
};

export const CompanyInfoForm = (props: CompanyInfoFormProps) => {
  const { isRedirected } = useGetCompanyInfo(props);
  const { formData } = props;
  const isFormValid = formData.initiativeCountryCode && formData.initiativeName && formData.unitConfig?.currency;

  return (
    <section className='form-container'>
      <Form className='mt-5'>
        <div className='row mb-3'>
          <CountrySelect {...props} isRedirected={isRedirected} />
        </div>
        <CompanyName {...props} />
        <CompanyAddress {...props} />
        <CompanyCurrency {...props} />
        <CompanyRegistrationNumber {...props} />
      </Form>
      <FormFooter {...props} isFormValid={!!isFormValid} />
    </section>
  );
};

export const CompanyInfoFormMAT = (props: CompanyInfoFormProps) => {
  const { isRedirected } = useGetCompanyInfo(props);
  const { formData } = props;
  const isFormValid = formData.initiativeCountryCode && formData.initiativeName;

  return (
    <section className='form-container'>
      <Form className='mt-5'>
        <CompanyName {...props} />
        <CompanyAddress {...props} />
        <div className='row'>
          <CountrySelect {...props} isRedirected={isRedirected} showLabel={false} />
        </div>
      </Form>
      <FormFooter {...props} isFormValid={!!isFormValid} />
    </section>
  );
};
